import React, { useState, useEffect, useCallback } from "react";
import { getBotStatus, startBot, stopBot, getDashboardMe, formatApiError } from "./api";
import Dashboard from "./pages/Dashboard";
import Scanner from "./pages/Scanner";
import Portfolio from "./pages/Portfolio";
import Backtest from "./pages/Backtest";
import Settings from "./pages/Settings";
import AdvisorChat from "./pages/AdvisorChat";
import DashboardLogin from "./DashboardLogin";
import useMediaQuery from "./hooks/useMediaQuery";

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: "▦" },
  { id: "scanner", label: "Scanner", icon: "⟳" },
  { id: "portfolio", label: "Portfolio", icon: "◈" },
  { id: "advisor", label: "Advisor", icon: "AI" },
  { id: "backtest", label: "Backtest", icon: "◉" },
  { id: "settings", label: "Settings", icon: "⚙" },
];

const TOAST_COLORS = { success: "#22c55e", warn: "#f59e0b", error: "#ef4444", info: "#38bdf8" };

function ToastStack({ toasts }) {
  return (
    <>
      <style>{`@keyframes fadeIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }`}</style>
      {toasts.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 14,
            right: 14,
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {toasts.map((t) => (
            <div
              key={t.id}
              style={{
                background: "#07111f",
                border: `1px solid ${TOAST_COLORS[t.type]}66`,
                color: TOAST_COLORS[t.type],
                borderRadius: 8,
                padding: "10px 16px",
                fontSize: 12,
                maxWidth: 360,
                fontFamily: "'DM Mono',monospace",
                whiteSpace: "pre-wrap",
                animation: "fadeIn 0.2s ease",
              }}
            >
              {t.msg}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default function App() {
  const [page, setPage] = useState("dashboard");
  const [dashboardSymbol, setDashboardSymbol] = useState(null);
  const [dashboardReturnPage, setDashboardReturnPage] = useState(null);
  const [botStatus, setBotStatus] = useState(null);
  const [toasts, setToasts] = useState([]);
  /** null = still loading /auth/dashboard/me; object = server response; never faked on network failure */
  const [dash, setDash] = useState(null);
  const [bootError, setBootError] = useState(null);
  const isMobile = useMediaQuery("(max-width: 760px)");

  const toast = useCallback((msg, type = "info") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 5000);
  }, []);

  const loadDashboardGate = useCallback(async () => {
    setBootError(null);
    setDash(null);
    try {
      const me = await getDashboardMe();
      setDash(me);
    } catch (e) {
      setBootError(formatApiError(e?.raw || e));
    }
  }, []);

  useEffect(() => {
    loadDashboardGate();
  }, [loadDashboardGate]);

  const fetchStatus = useCallback(async () => {
    try {
      setBotStatus(await getBotStatus());
    } catch (e) {
      if (e?.needsDashboardLogin) {
        setDash((d) => (d && typeof d === "object" ? { ...d, loggedIn: false } : d));
      }
      setBotStatus(null);
    }
  }, []);

  const gateOk = dash && (!dash.enabled || dash.loggedIn);

  useEffect(() => {
    if (!gateOk) return undefined;
    fetchStatus();
    const t = setInterval(fetchStatus, 6000);
    return () => clearInterval(t);
  }, [gateOk, fetchStatus]);

  const handleBotToggle = async () => {
    try {
      if (botStatus?.botActive) {
        await stopBot();
        toast("Bot stopped", "warn");
      } else {
        await startBot();
        toast("Bot started!", "success");
      }
      fetchStatus();
    } catch (e) {
      if (e?.needsDashboardLogin) {
        setDash((d) => (d && typeof d === "object" ? { ...d, loggedIn: false } : d));
        toast("Dashboard session expired — sign in again.", "warn");
        return;
      }
      toast(e?.message || formatApiError(e), "error");
    }
  };

  const openDashboardSymbol = useCallback((selection) => {
    const payload = typeof selection === "string" ? { symbol: selection } : selection || {};
    const sym = String(payload.symbol || "").trim().toUpperCase();
    if (!sym) return;
    const returnPage = payload.returnPage || (page !== "dashboard" ? page : null);
    setDashboardSymbol({
      symbol: sym,
      instrumentKey: payload.instrumentKey || "",
      trade: payload.trade || null,
      requestId: Date.now(),
    });
    setDashboardReturnPage(returnPage);
    setPage("dashboard");
  }, [page]);

  if (bootError) {
    return (
      <>
        <ToastStack toasts={toasts} />
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#04080f",
            padding: 24,
          }}
        >
          <div
            style={{
              maxWidth: 440,
              background: "#060d1a",
              border: "1px solid #7f1d1d55",
              borderRadius: 12,
              padding: 24,
              color: "#fca5a5",
              fontFamily: "'DM Mono',monospace",
              fontSize: 12,
              lineHeight: 1.65,
            }}
          >
            <div style={{ fontWeight: 700, color: "#f87171", marginBottom: 10 }}>Cannot reach the API</div>
            <div style={{ color: "#94a3b8" }}>{bootError}</div>
            <div style={{ marginTop: 14, fontSize: 11, color: "#64748b" }}>
              Dashboard login is controlled by the <strong style={{ color: "#94a3b8" }}>backend</strong>{" "}
              <code style={{ color: "#f59e0b" }}>.env</code> (<code style={{ color: "#f59e0b" }}>DASHBOARD_USERNAME</code> /{" "}
              <code style={{ color: "#f59e0b" }}>DASHBOARD_PASSWORD</code>). Putting those only in the React{" "}
              <code style={{ color: "#f59e0b" }}>.env</code> does nothing — restart the Node server after changing backend env.
            </div>
            <button
              type="button"
              onClick={() => loadDashboardGate()}
              style={{
                marginTop: 18,
                padding: "10px 18px",
                borderRadius: 8,
                border: "1px solid #334155",
                background: "#0f172a",
                color: "#e2e8f0",
                cursor: "pointer",
                fontFamily: "'DM Mono',monospace",
                fontSize: 12,
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  if (dash === null) {
    return (
      <>
        <ToastStack toasts={toasts} />
        <div
          style={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#04080f",
            color: "#475569",
            fontFamily: "'DM Mono',monospace",
            fontSize: 12,
          }}
        >
          Loading…
        </div>
      </>
    );
  }

  if (dash.enabled && !dash.loggedIn) {
    return (
      <>
        <ToastStack toasts={toasts} />
        <DashboardLogin
          hint={dash.hint}
          onSuccess={async () => {
            try {
              const me = await getDashboardMe();
              setDash(me);
            } catch (e) {
              toast(formatApiError(e?.raw || e), "error");
            }
          }}
        />
      </>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", height: isMobile ? "auto" : "100vh", background: "#04080f" }}>
      <ToastStack toasts={toasts} />

      <header
        style={{
          background: "#060d1a",
          borderBottom: "1px solid #0f172a",
          padding: isMobile ? "10px 12px" : "0 20px",
          minHeight: 54,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: isMobile ? 10 : 14,
          flexWrap: isMobile ? "wrap" : "nowrap",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 30,
              height: 30,
              background: "linear-gradient(135deg,#22c55e,#0ea5e9)",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
            }}
          >
            ⚡
          </div>
          <div>
            <div
              style={{
                fontFamily: "'Syne',sans-serif",
                fontSize: 15,
                fontWeight: 800,
                color: "#f1f5f9",
                letterSpacing: "0.02em",
              }}
            >
              ALGO<span style={{ color: "#22c55e" }}>TRADE</span>
            </div>
            <div style={{ fontSize: 9, color: "#334155", letterSpacing: "0.1em" }}>UPSTOX · NSE · BSE</div>
          </div>
        </div>

        <nav style={{ display: "flex", gap: 2, overflowX: "auto", flex: isMobile ? "1 0 100%" : "0 1 auto", order: isMobile ? 3 : 0, paddingBottom: isMobile ? 2 : 0 }}>
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => {
                setPage(n.id);
                setDashboardReturnPage(null);
              }}
              style={{
                background: page === n.id ? "#0d1f35" : "transparent",
                border: "none",
                borderBottom: `2px solid ${page === n.id ? "#22c55e" : "transparent"}`,
                color: page === n.id ? "#22c55e" : "#475569",
                padding: isMobile ? "8px 10px" : "16px 14px 14px",
                cursor: "pointer",
                fontFamily: "'DM Mono',monospace",
                fontSize: isMobile ? 10 : 11,
                fontWeight: page === n.id ? 600 : 400,
                letterSpacing: "0.05em",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "color 0.15s",
              }}
            >
              <span style={{ fontSize: 13 }}>{n.icon}</span>
              {n.label}
            </button>
          ))}
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 14, marginLeft: isMobile ? "auto" : 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {!isMobile && <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: "#475569" }}>
            <div
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: botStatus?.marketOpen ? "#22c55e" : "#475569",
                boxShadow: botStatus?.marketOpen ? "0 0 6px #22c55e" : "none",
              }}
            />
            {botStatus?.marketOpen ? "MARKET OPEN" : "MARKET CLOSED"}
          </div>}

          {botStatus?.paperTrading && (
            <div
              style={{
                background: "#1f1200",
                border: "1px solid #f59e0b44",
                color: "#f59e0b",
                borderRadius: 4,
                padding: "3px 8px",
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.08em",
              }}
            >
              PAPER
            </div>
          )}

          <button
            onClick={handleBotToggle}
            style={{
              background: botStatus?.botActive ? "#052e16" : "#0d1f35",
              border: `1px solid ${botStatus?.botActive ? "#22c55e" : "#334155"}`,
              color: botStatus?.botActive ? "#22c55e" : "#64748b",
              borderRadius: 8,
              padding: isMobile ? "6px 10px" : "6px 16px",
              cursor: "pointer",
              fontFamily: "'DM Mono',monospace",
              fontSize: isMobile ? 10 : 11,
              fontWeight: 700,
              letterSpacing: "0.05em",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: botStatus?.botActive ? "#22c55e" : "#475569",
                boxShadow: botStatus?.botActive ? "0 0 8px #22c55e" : "none",
                animation: botStatus?.botActive ? "pulse 1.5s infinite" : "none",
              }}
            />
            {botStatus?.botActive ? "BOT LIVE" : "BOT OFF"}
          </button>
        </div>
      </header>

      <main style={{ flex: 1, overflow: "auto", padding: isMobile ? 10 : 16, minWidth: 0 }}>
        {page === "dashboard" && (
          <Dashboard
            botStatus={botStatus}
            selectedSymbol={dashboardSymbol?.symbol || dashboardSymbol}
            selectedInstrumentKey={dashboardSymbol?.instrumentKey || ""}
            selectedTrade={dashboardSymbol?.trade || null}
            selectedRequestId={dashboardSymbol?.requestId || 0}
            returnPage={dashboardReturnPage}
            onBack={dashboardReturnPage ? () => {
              setPage(dashboardReturnPage);
              setDashboardReturnPage(null);
            } : null}
          />
        )}
        {page === "scanner" && <Scanner botStatus={botStatus} onRefresh={fetchStatus} onOpenSymbol={openDashboardSymbol} />}
        {page === "portfolio" && <Portfolio botStatus={botStatus} onOpenSymbol={openDashboardSymbol} />}
        {page === "advisor" && <AdvisorChat botStatus={botStatus} />}
        {page === "backtest" && <Backtest botStatus={botStatus} />}
        {page === "settings" && <Settings botStatus={botStatus} onRefresh={fetchStatus} dashboard={dash} />}
      </main>

      <footer
        style={{
          background: "#060d1a",
          borderTop: "1px solid #0a1628",
          padding: isMobile ? "6px 10px 76px" : "5px 20px",
          display: "flex",
          alignItems: "center",
          gap: isMobile ? 10 : 20,
          fontSize: 10,
          color: "#334155",
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        <span style={{ color: botStatus ? "#22c55e" : "#ef4444" }}>
          ● {botStatus ? "Backend connected" : "Backend offline"}
        </span>
        <span>
          WS:{" "}
          {botStatus?.ws?.connected ? (
            <span style={{ color: "#22c55e" }}>connected</span>
          ) : (
            <span style={{ color: "#475569" }}>disconnected</span>
          )}
        </span>
        <span>Instruments: {botStatus?.ws?.instruments || 0}</span>
        <span>
          Positions: {botStatus?.openPositions || 0}/{botStatus?.maxPositions || 5}
        </span>
        {dash?.enabled && (
          <span style={{ color: "#64748b" }}>
            Dashboard gate: <span style={{ color: "#22c55e" }}>on</span>
          </span>
        )}
        <span style={{ marginLeft: "auto" }}>AlgoTrade v2.0 · Upstox India</span>
      </footer>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.35} }
      `}</style>
    </div>
  );
}
